import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_URL } from '../config';

// ─── Helpers ─────────────────────────────────────────────────────────────────

function buildHierarchy(items, parentId = null) {
  return items
    .filter(i => i.parentId === parentId)
    .slice()
    .sort((a, b) => a.order - b.order)
    .map(item => ({ ...item, children: buildHierarchy(items, item._id) }));
}

function findNodeInTree(nodes, id) {
  for (const n of nodes) {
    if (n._id === id) return n;
    const found = findNodeInTree(n.children || [], id);
    if (found) return found;
  }
  return null;
}

function countDescendants(node) {
  if (!node.children?.length) return 0;
  return node.children.reduce((s, c) => s + 1 + countDescendants(c), 0);
}

// Is `testId` a descendant of `ancestorId`?
function isDescendantOf(items, testId, ancestorId) {
  const node = items.find(k => k._id === testId);
  if (!node || !node.parentId) return false;
  if (node.parentId === ancestorId) return true;
  return isDescendantOf(items, node.parentId, ancestorId);
}

// ─── Icons ───────────────────────────────────────────────────────────────────

const ChevronRight = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="9 18 15 12 9 6"/>
  </svg>
);
const ChevronDown = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="6 9 12 15 18 9"/>
  </svg>
);
const FolderIcon = ({ color = '#8B5CF6', open = false, size = 15 }) =>
  open ? (
    <svg width={size} height={size} viewBox="0 0 24 24" fill={color} stroke={color} strokeWidth="1" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/>
    </svg>
  ) : (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/>
    </svg>
  );
const TagIcon = ({ color = '#10B981', size = 13 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={color} stroke="none">
    <circle cx="9" cy="9" r="2.2"/>
    <path d="M4 4h7l9 9-7 7-9-9V4z"/>
  </svg>
);
const EditIcon = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
  </svg>
);
const TrashIcon = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14H6L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4h6v2"/>
  </svg>
);
const MoveIcon = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="5 9 2 12 5 15"/><polyline points="9 5 12 2 15 5"/><polyline points="15 19 12 22 9 19"/><polyline points="19 9 22 12 19 15"/>
    <line x1="2" y1="12" x2="22" y2="12"/><line x1="12" y1="2" x2="12" y2="22"/>
  </svg>
);

// ─── Sidebar tree row ────────────────────────────────────────────────────────

function SidebarNode({ node, depth, selectedId, onSelect, expandedIds, onToggle }) {
  const isExpanded = expandedIds.has(node._id);
  const hasChildren = node.children?.length > 0;
  const isSelected = selectedId === node._id;

  return (
    <div>
      <div
        className={`flex items-center gap-1 rounded cursor-pointer text-sm select-none
          ${isSelected
            ? 'bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 font-semibold'
            : 'hover:bg-gray-100 dark:hover:bg-gray-700/50 text-gray-700 dark:text-gray-300'
          }`}
        style={{ paddingTop: 3, paddingBottom: 3, paddingLeft: depth * 14 + 4, paddingRight: 6 }}
        onClick={() => onSelect(node)}
      >
        <span
          className="flex-shrink-0 w-4 h-4 flex items-center justify-center text-gray-400 hover:text-gray-600"
          onClick={e => { e.stopPropagation(); if (hasChildren) onToggle(node._id); }}
        >
          {hasChildren
            ? (isExpanded ? <ChevronDown size={12}/> : <ChevronRight size={12}/>)
            : <span className="w-3 inline-block"/>
          }
        </span>
        <span className="flex-shrink-0">
          {hasChildren
            ? <FolderIcon color={node.color || '#8B5CF6'} open={isExpanded} size={14}/>
            : <TagIcon color={node.color || '#10B981'} size={12}/>
          }
        </span>
        <span className="truncate flex-1 min-w-0 ml-1 leading-snug">{node.name}</span>
        <span className="flex-shrink-0 text-xs text-gray-400 dark:text-gray-600 ml-1">
          {countDescendants(node) > 0 ? countDescendants(node) : ''}
        </span>
      </div>
      {isExpanded && hasChildren && node.children.map(child => (
        <SidebarNode
          key={child._id}
          node={child}
          depth={depth + 1}
          selectedId={selectedId}
          onSelect={onSelect}
          expandedIds={expandedIds}
          onToggle={onToggle}
        />
      ))}
    </div>
  );
}

// ─── Move-to tree picker (modal) ─────────────────────────────────────────────

function MoveTreeNode({ node, targetId, onSelect, disabledIds }) {
  const [open, setOpen] = useState(false);
  const hasChildren = node.children?.length > 0;
  const isDisabled = disabledIds.has(node._id);
  const isSelected = targetId === node._id;

  return (
    <div>
      <div
        className={`flex items-center gap-1.5 py-1 px-2 rounded text-sm cursor-pointer select-none
          ${isDisabled ? 'opacity-30 pointer-events-none' : ''}
          ${isSelected ? 'bg-blue-100 dark:bg-blue-800/60 text-blue-700 dark:text-blue-300 font-medium' : 'hover:bg-gray-100 dark:hover:bg-gray-700/50 text-gray-700 dark:text-gray-300'}
        `}
        onClick={() => !isDisabled && onSelect(node._id)}
      >
        {hasChildren
          ? <button className="flex-shrink-0" onClick={e => { e.stopPropagation(); setOpen(o => !o); }}>
              {open ? <ChevronDown size={12}/> : <ChevronRight size={12}/>}
            </button>
          : <span className="w-3 inline-block flex-shrink-0"/>
        }
        <span className="flex-shrink-0">
          {hasChildren ? <FolderIcon color={node.color || '#8B5CF6'} size={13}/> : <TagIcon color={node.color || '#10B981'} size={11}/>}
        </span>
        <span className="truncate">{node.name}</span>
        {isSelected && <span className="ml-auto text-blue-500">✓</span>}
      </div>
      {open && hasChildren && node.children.map(child => (
        <div key={child._id} className="pl-4">
          <MoveTreeNode node={child} targetId={targetId} onSelect={onSelect} disabledIds={disabledIds}/>
        </div>
      ))}
    </div>
  );
}

// ─── Knowledge admin card ────────────────────────────────────────────────────

function KnowledgeCard({ node, onDrillDown, onEdit, onDelete, onAddChild, onMove }) {
  const hasChildren = node.children?.length > 0;
  const total = countDescendants(node);

  return (
    <div
      className="relative flex flex-col rounded-xl border-2 border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800
        hover:border-blue-300 dark:hover:border-blue-600 hover:shadow-md transition-all duration-150 overflow-hidden group"
    >
      {/* color strip */}
      <div className="h-1 w-full flex-shrink-0" style={{ backgroundColor: node.color || '#8B5CF6' }}/>

      <div className="p-3 flex flex-col flex-1">
        {/* name */}
        <div className="flex items-start gap-2 mb-1">
          <span className="flex-shrink-0 mt-0.5">
            {hasChildren
              ? <FolderIcon color={node.color || '#8B5CF6'} size={15}/>
              : <TagIcon color={node.color || '#10B981'} size={13}/>
            }
          </span>
          <span
            className={`flex-1 min-w-0 font-semibold text-sm leading-snug break-words
              ${hasChildren ? 'text-blue-600 dark:text-blue-400 cursor-pointer hover:underline' : 'text-gray-800 dark:text-gray-100 cursor-default'}
            `}
            onClick={() => hasChildren && onDrillDown(node)}
            title={hasChildren ? `Otevřít: ${node.name}` : undefined}
          >
            {node.name}
          </span>
        </div>

        {/* description */}
        {node.description && (
          <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-2 pl-5 mb-1">
            {node.description}
          </p>
        )}

        {/* sub-count + drill link */}
        {total > 0 && (
          <div className="flex items-center justify-between pl-5 mt-1">
            <span className="text-xs text-gray-400 dark:text-gray-500">{total} podpoložek</span>
            {hasChildren && (
              <button
                onClick={() => onDrillDown(node)}
                className="text-xs text-blue-500 dark:text-blue-400 hover:underline"
              >
                Otevřít →
              </button>
            )}
          </div>
        )}
      </div>

      {/* action toolbar — visible on hover */}
      <div className="flex-shrink-0 border-t border-gray-100 dark:border-gray-700 px-2 py-1.5 flex items-center gap-1
        bg-gray-50 dark:bg-gray-800/80 opacity-0 group-hover:opacity-100 transition-opacity">
        <button
          onClick={() => onAddChild(node)}
          className="flex items-center gap-1 px-2 py-1 rounded text-xs text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/30 transition"
          title="Přidat pod tuto položku"
        >
          <span>➕</span><span className="hidden sm:inline">Přidat</span>
        </button>
        <button
          onClick={() => onEdit(node)}
          className="flex items-center gap-1 px-2 py-1 rounded text-xs text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 transition"
          title="Upravit"
        >
          <EditIcon size={12}/><span className="hidden sm:inline">Upravit</span>
        </button>
        <button
          onClick={() => onMove(node)}
          className="flex items-center gap-1 px-2 py-1 rounded text-xs text-amber-600 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/30 transition"
          title="Přesunout"
        >
          <MoveIcon size={12}/><span className="hidden sm:inline">Přesunout</span>
        </button>
        <button
          onClick={() => onDelete(node)}
          className="ml-auto flex items-center gap-1 px-2 py-1 rounded text-xs text-red-500 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/30 transition"
          title="Smazat"
        >
          <TrashIcon size={12}/><span className="hidden sm:inline">Smazat</span>
        </button>
      </div>
    </div>
  );
}

// ─── Main component ──────────────────────────────────────────────────────────

function MindMapKnowledgeManager({ isDarkMode, onClose, userRole, userId }) {
  const navigate = useNavigate();
  const [knowledge, setKnowledge] = useState([]);
  const [loading, setLoading]     = useState(true);

  // Navigation
  const [navStack, setNavStack]     = useState([]);
  const [expandedIds, setExpandedIds] = useState(new Set());
  const [search, setSearch]           = useState('');

  // Modals
  const [editingNode, setEditingNode] = useState(null);
  const [editForm, setEditForm]       = useState({ name: '', color: '', description: '' });
  const [addingChild, setAddingChild] = useState(null); // null = root, else parent node
  const [addForm, setAddForm]         = useState({ name: '', color: '', description: '' });
  const [movingNode, setMovingNode]   = useState(null);
  const [moveTarget, setMoveTarget]   = useState(null); // _id or null (= root)
  const [addRootOpen, setAddRootOpen] = useState(false);
  const [addRootForm, setAddRootForm] = useState({ name: '', color: '#8B5CF6', description: '' });

  const predefinedColors = [
    { name: 'Fialová',          value: '#8B5CF6' },
    { name: 'Oranžová',         value: '#F59E0B' },
    { name: 'Zelená',           value: '#10B981' },
    { name: 'Modrá',            value: '#3B82F6' },
    { name: 'Červená',          value: '#EF4444' },
    { name: 'Růžová',           value: '#EC4899' },
    { name: 'Žlutá',            value: '#EAB308' },
    { name: 'Tyrkysová',        value: '#06B6D4' },
    { name: 'Fialovo-růžová',   value: '#A855F7' },
    { name: 'Limetkově zelená', value: '#84CC16' },
    { name: 'Oranžově červená', value: '#F97316' },
    { name: 'Šedá',             value: '#6B7280' },
    { name: 'Tmavě modrá',      value: '#1E40AF' },
    { name: 'Hnědá',            value: '#92400E' },
    { name: 'Tmavě zelená',     value: '#166534' },
  ];

  const getDefaultColor = (type) =>
    ({ category: '#8B5CF6', subcategory: '#F59E0B', skill: '#10B981' }[type] || '#6B7280');

  // ── data ─────────────────────────────────────────────────────────────────

  const loadKnowledge = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/knowledge?userId=${userId || localStorage.getItem('lastUserId') || ''}`);
      if (res.ok) setKnowledge(await res.json());
    } catch (err) {
      console.error('Error loading knowledge:', err);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => { loadKnowledge(); }, [loadKnowledge]);

  // ── derived ──────────────────────────────────────────────────────────────

  const hierarchy = buildHierarchy(knowledge);

  const currentParent   = navStack[navStack.length - 1] ?? null;
  const currentChildren = currentParent
    ? (findNodeInTree(hierarchy, currentParent._id)?.children ?? [])
    : hierarchy;

  const filteredChildren = search.trim()
    ? currentChildren.filter(n => n.name.toLowerCase().includes(search.toLowerCase()))
    : currentChildren;

  const totalItems = knowledge.length;

  // ── navigation ───────────────────────────────────────────────────────────

  const drillDown = (node) => {
    setNavStack(prev => [...prev, node]);
    setSearch('');
    setExpandedIds(prev => new Set([...prev, node._id]));
  };

  const navTo = (index) => {
    setNavStack(prev => prev.slice(0, index));
    setSearch('');
  };

  const sidebarSelect = (node) => {
    setNavStack([node]);
    setSearch('');
    setExpandedIds(prev => new Set([...prev, node._id]));
  };

  const toggleExpanded = (id) =>
    setExpandedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  // ── CRUD ─────────────────────────────────────────────────────────────────

  const initializeKnowledge = async () => {
    try {
      const res = await fetch(`${API_URL}/knowledge/initialize?userId=${userId || localStorage.getItem('lastUserId') || ''}`, { method: 'POST' });
      if (res.ok) { await loadKnowledge(); alert('✅ Struktura znalostí vytvořena!'); }
      else alert('❌ Chyba při vytváření struktury');
    } catch (err) { console.error(err); alert('❌ Chyba při vytváření struktury'); }
  };

  const handleAddRoot = async () => {
    if (!addRootForm.name.trim()) return;
    try {
      const res = await fetch(`${API_URL}/knowledge?userId=${userId || localStorage.getItem('lastUserId') || ''}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: addRootForm.name.trim(),
          type: 'category',
          parentId: null,
          color: addRootForm.color,
          description: addRootForm.description,
          order: knowledge.filter(k => k.parentId === null).length + 1,
        }),
      });
      if (res.ok) { await loadKnowledge(); setAddRootOpen(false); setAddRootForm({ name: '', color: '#8B5CF6', description: '' }); }
    } catch (err) { console.error(err); }
  };

  const handleAddChild = async () => {
    if (!addForm.name.trim()) return;
    const parent = addingChild;
    const nextType = parent.type === 'category' ? 'subcategory' : 'skill';
    try {
      const res = await fetch(`${API_URL}/knowledge?userId=${userId || localStorage.getItem('lastUserId') || ''}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: addForm.name.trim(),
          type: nextType,
          parentId: parent._id,
          color: addForm.color,
          description: addForm.description,
          order: knowledge.filter(k => k.parentId === parent._id).length + 1,
        }),
      });
      if (res.ok) { await loadKnowledge(); setAddingChild(null); setAddForm({ name: '', color: '', description: '' }); }
    } catch (err) { console.error(err); }
  };

  const handleUpdateNode = async () => {
    try {
      const res = await fetch(`${API_URL}/knowledge/${editingNode._id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: editForm.name, color: editForm.color, description: editForm.description }),
      });
      if (res.ok) { await loadKnowledge(); setEditingNode(null); }
    } catch (err) { console.error(err); alert('❌ Chyba při ukládání'); }
  };

  const handleDeleteNode = async (node) => {
    const desc = countDescendants(findNodeInTree(hierarchy, node._id) || node);
    if (!window.confirm(`Smazat "${node.name}"?${desc > 0 ? `\nSmaže se i ${desc} podpoložek.` : ''}`)) return;
    try {
      const res = await fetch(`${API_URL}/knowledge/${node._id}`, { method: 'DELETE' });
      if (res.ok) {
        await loadKnowledge();
        // If we deleted current parent, go back
        if (navStack.some(n => n._id === node._id)) {
          setNavStack(prev => prev.filter(n => n._id !== node._id));
        }
      }
    } catch (err) { console.error(err); alert('❌ Chyba při mazání'); }
  };

  const handleMoveNode = async () => {
    if (!movingNode) return;
    // moveTarget: null = root, string = parentId
    try {
      const newOrder = knowledge.filter(k => k.parentId === moveTarget).length + 1;
      const res = await fetch(`${API_URL}/knowledge/${movingNode._id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: movingNode.name,
          type: movingNode.type,
          parentId: moveTarget,
          color: movingNode.color,
          description: movingNode.description,
          order: newOrder,
        }),
      });
      if (res.ok) { await loadKnowledge(); setMovingNode(null); setMoveTarget(null); }
    } catch (err) { console.error(err); alert('❌ Chyba při přesunu'); }
  };

  const openEdit = (node) => {
    setEditingNode(node);
    setEditForm({ name: node.name, color: node.color || getDefaultColor(node.type), description: node.description || '' });
  };

  const openAddChild = (node) => {
    const nextType = node.type === 'category' ? 'subcategory' : 'skill';
    setAddingChild(node);
    setAddForm({ name: '', color: getDefaultColor(nextType), description: '' });
  };

  const openMove = (node) => {
    setMovingNode(node);
    setMoveTarget(node.parentId ?? null);
  };

  // ── role guard ───────────────────────────────────────────────────────────

  if (userRole !== 'admin' && userRole !== 'teamleader' && userRole !== 'superadmin') {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-gray-50 dark:bg-gray-900 z-50">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-red-600 mb-3">🚫 Přístup odepřen</h1>
          <p className="text-gray-500 mb-4">Stránka je dostupná pouze administrátorům.</p>
          <button onClick={() => onClose ? onClose() : navigate('/')} className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg">Zpět</button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center z-50">
        <div className="bg-white dark:bg-gray-800 rounded-xl p-8 flex flex-col items-center shadow-2xl">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent mb-4"/>
          <p className="text-lg font-semibold text-gray-700 dark:text-gray-200">Načítám strukturu znalostí…</p>
        </div>
      </div>
    );
  }

  // Disabled IDs for move dialog: the node itself + all its descendants
  const movingNodeDisabledIds = movingNode
    ? new Set([movingNode._id, ...knowledge.filter(k => isDescendantOf(knowledge, k._id, movingNode._id)).map(k => k._id)])
    : new Set();

  // ── render ────────────────────────────────────────────────────────────────

  return (
    <div className="fixed inset-0 flex flex-col z-50" style={{ background: isDarkMode ? '#111827' : '#F3F4F6' }}>

      {/* ══ TOP BAR ════════════════════════════════════════════════════════ */}
      <div className="flex-shrink-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 py-2.5 flex items-center gap-3 shadow-sm">
        <button
          onClick={() => onClose ? onClose() : navigate('/')}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 text-sm font-medium transition"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7"/>
          </svg>
          Zpět
        </button>

        <div className="text-lg font-bold text-gray-900 dark:text-white flex-1 min-w-0 truncate">
          🧠 Správa znalostí
        </div>

        <div className="flex items-center gap-2 flex-shrink-0">
          <span className="text-sm text-gray-500 dark:text-gray-400 hidden sm:inline">
            {totalItems} položek
          </span>
          <button
            onClick={() => setAddRootOpen(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-purple-500 hover:bg-purple-600 text-white text-sm font-medium transition shadow-sm"
          >
            ➕ Nová kategorie
          </button>
          {knowledge.length === 0 && (
            <button
              onClick={initializeKnowledge}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-blue-500 hover:bg-blue-600 text-white text-sm font-medium transition"
            >
              🚀 Inicializovat
            </button>
          )}
        </div>
      </div>

      {/* ══ BODY ═══════════════════════════════════════════════════════════ */}
      <div className="flex flex-1 min-h-0 overflow-hidden">

        {/* ── LEFT SIDEBAR ─────────────────────────────────────────────── */}
        <div className="w-56 xl:w-64 flex-shrink-0 bg-white dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 flex flex-col">
          <div className="px-3 pt-2.5 pb-1.5 border-b border-gray-100 dark:border-gray-700">
            <span className="text-xs font-semibold uppercase tracking-wide text-gray-400 dark:text-gray-500">
              Navigace
            </span>
          </div>
          <div className="flex-1 overflow-y-auto py-1 px-1 text-[13px]">
            {/* root */}
            <div
              className={`flex items-center gap-1.5 px-2 py-1 rounded cursor-pointer select-none text-sm font-medium
                ${navStack.length === 0
                  ? 'bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300'
                  : 'hover:bg-gray-100 dark:hover:bg-gray-700/50 text-gray-600 dark:text-gray-400'
                }`}
              onClick={() => navTo(0)}
            >
              <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V9z"/>
              </svg>
              <span>Všechny znalosti</span>
            </div>
            {hierarchy.map(node => (
              <SidebarNode
                key={node._id}
                node={node}
                depth={0}
                selectedId={currentParent?._id ?? null}
                onSelect={sidebarSelect}
                expandedIds={expandedIds}
                onToggle={toggleExpanded}
              />
            ))}
          </div>
        </div>

        {/* ── RIGHT PANEL ──────────────────────────────────────────────── */}
        <div className="flex-1 min-w-0 flex flex-col">

          {/* breadcrumb + search */}
          <div className="flex-shrink-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-4 py-2 flex items-center gap-3 flex-wrap">
            <nav className="flex items-center gap-0.5 text-sm flex-1 min-w-0 overflow-x-auto whitespace-nowrap">
              <button className="flex-shrink-0 text-blue-600 dark:text-blue-400 hover:underline font-medium" onClick={() => navTo(0)}>
                Znalosti
              </button>
              {navStack.map((node, i) => {
                const isLast = i === navStack.length - 1;
                return (
                  <React.Fragment key={node._id}>
                    <span className="flex-shrink-0 text-gray-400 mx-0.5"><ChevronRight size={12}/></span>
                    <button
                      className={`flex-shrink-0 font-medium truncate max-w-[160px] ${isLast ? 'text-gray-700 dark:text-gray-200 pointer-events-none' : 'text-blue-600 dark:text-blue-400 hover:underline'}`}
                      onClick={() => !isLast && navTo(i + 1)}
                    >
                      {node.name}
                    </button>
                  </React.Fragment>
                );
              })}
            </nav>

            {/* add child in current folder */}
            {currentParent && (
              <button
                onClick={() => openAddChild(findNodeInTree(hierarchy, currentParent._id) || currentParent)}
                className="flex-shrink-0 text-xs px-2.5 py-1 rounded-lg bg-green-50 dark:bg-green-900/40 text-green-600 dark:text-green-400 hover:bg-green-100 dark:hover:bg-green-900/60 font-medium transition border border-green-200 dark:border-green-800"
              >
                ➕ Přidat do složky
              </button>
            )}

            {/* search */}
            <div className="flex-shrink-0 relative">
              <svg className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 w-3.5 h-3.5 pointer-events-none" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
              </svg>
              <input
                type="text"
                placeholder="Hledat v úrovni…"
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="pl-8 pr-7 py-1.5 text-sm rounded-lg border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700 text-gray-800 dark:text-gray-200 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-400 w-44"
              />
              {search && (
                <button onClick={() => setSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700 dark:hover:text-gray-100 text-base leading-none">×</button>
              )}
            </div>
          </div>

          {/* context strip */}
          {currentParent && (
            <div
              className="flex-shrink-0 px-4 py-1.5 flex items-center gap-2 border-b border-gray-100 dark:border-gray-700/60"
              style={{ background: (currentParent.color || '#8B5CF6') + '14' }}
            >
              <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: currentParent.color || '#8B5CF6' }}/>
              <span className="text-sm font-semibold text-gray-800 dark:text-gray-200 truncate">{currentParent.name}</span>
              {currentParent.description && (
                <span className="text-xs text-gray-500 dark:text-gray-400 truncate hidden md:inline">
                  — {currentParent.description}
                </span>
              )}
              <div className="ml-auto flex gap-1.5 flex-shrink-0">
                <button
                  onClick={() => openEdit(findNodeInTree(hierarchy, currentParent._id) || currentParent)}
                  className="text-xs px-2 py-1 rounded bg-blue-50 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400 hover:bg-blue-100 transition border border-blue-200 dark:border-blue-800"
                >
                  ✏️ Upravit
                </button>
                <button
                  onClick={() => handleDeleteNode(currentParent)}
                  className="text-xs px-2 py-1 rounded bg-red-50 dark:bg-red-900/40 text-red-600 dark:text-red-400 hover:bg-red-100 transition border border-red-200 dark:border-red-800"
                >
                  🗑️ Smazat
                </button>
              </div>
            </div>
          )}

          {/* cards grid */}
          <div className="flex-1 overflow-y-auto p-4">
            {knowledge.length === 0 ? (
              <div className="h-full flex flex-col items-center justify-center text-gray-400 dark:text-gray-600 py-20">
                <svg className="w-16 h-16 mb-4 opacity-30" fill="none" stroke="currentColor" strokeWidth="1.2" viewBox="0 0 24 24">
                  <rect x="3" y="3" width="18" height="18" rx="3"/>
                  <path d="M9 12h6M12 9v6"/>
                </svg>
                <p className="text-sm font-medium mb-3">Žádná struktura znalostí</p>
                <button
                  onClick={initializeKnowledge}
                  className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg text-sm font-medium transition"
                >
                  🚀 Inicializovat strukturu
                </button>
              </div>
            ) : filteredChildren.length === 0 ? (
              <div className="h-full flex flex-col items-center justify-center text-gray-400 dark:text-gray-600 py-20">
                <svg className="w-12 h-12 mb-3 opacity-30" fill="none" stroke="currentColor" strokeWidth="1.2" viewBox="0 0 24 24">
                  <rect x="3" y="3" width="18" height="18" rx="3"/>
                  <path d="M9 9h6M9 12h6M9 15h4"/>
                </svg>
                <p className="text-sm font-medium">{search ? 'Nic nenalezeno' : 'Složka je prázdná'}</p>
                {search && <button onClick={() => setSearch('')} className="mt-2 text-xs text-blue-500 hover:underline">Zrušit filtr</button>}
                {!search && currentParent && (
                  <button
                    onClick={() => openAddChild(findNodeInTree(hierarchy, currentParent._id) || currentParent)}
                    className="mt-3 px-3 py-1.5 bg-green-500 hover:bg-green-600 text-white rounded-lg text-xs font-medium transition"
                  >
                    ➕ Přidat první položku
                  </button>
                )}
              </div>
            ) : (
              <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(210px, 1fr))' }}>
                {filteredChildren.map(node => (
                  <KnowledgeCard
                    key={node._id}
                    node={node}
                    onDrillDown={drillDown}
                    onEdit={openEdit}
                    onDelete={handleDeleteNode}
                    onAddChild={openAddChild}
                    onMove={openMove}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ══ ADD ROOT MODAL ═════════════════════════════════════════════════ */}
      {addRootOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-2xl w-full max-w-sm">
            <h3 className="text-base font-bold mb-4 text-gray-900 dark:text-white">➕ Nová kategorie</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">Název:</label>
                <input
                  type="text" value={addRootForm.name}
                  onChange={e => setAddRootForm(p => ({ ...p, name: e.target.value }))}
                  className="w-full p-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-purple-400"
                  placeholder="Název kategorie…" autoFocus
                  onKeyDown={e => e.key === 'Enter' && handleAddRoot()}
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">Popis:</label>
                <textarea value={addRootForm.description} onChange={e => setAddRootForm(p => ({ ...p, description: e.target.value }))}
                  className="w-full p-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm resize-none focus:outline-none focus:ring-2 focus:ring-purple-400"
                  rows="2" placeholder="Popis…"/>
              </div>
              <ColorPicker colors={predefinedColors} selected={addRootForm.color} onSelect={c => setAddRootForm(p => ({ ...p, color: c }))}/>
              <div className="flex gap-2">
                <button onClick={handleAddRoot} disabled={!addRootForm.name.trim()} className="flex-1 bg-purple-500 hover:bg-purple-600 disabled:opacity-40 text-white px-4 py-2 rounded-lg text-sm font-medium transition">✅ Vytvořit</button>
                <button onClick={() => setAddRootOpen(false)} className="flex-1 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 px-4 py-2 rounded-lg text-sm font-medium transition">Zrušit</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ══ ADD CHILD MODAL ════════════════════════════════════════════════ */}
      {addingChild && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-2xl w-full max-w-sm">
            <h3 className="text-base font-bold mb-4 text-gray-900 dark:text-white">
              ➕ Pod: <span style={{ color: addingChild.color || '#8B5CF6' }}>{addingChild.name}</span>
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">Název:</label>
                <input type="text" value={addForm.name} onChange={e => setAddForm(p => ({ ...p, name: e.target.value }))}
                  className="w-full p-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-green-400"
                  placeholder="Název…" autoFocus onKeyDown={e => e.key === 'Enter' && handleAddChild()}/>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">Popis:</label>
                <textarea value={addForm.description} onChange={e => setAddForm(p => ({ ...p, description: e.target.value }))}
                  className="w-full p-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm resize-none focus:outline-none focus:ring-2 focus:ring-green-400"
                  rows="2" placeholder="Popis…"/>
              </div>
              <ColorPicker colors={predefinedColors} selected={addForm.color} onSelect={c => setAddForm(p => ({ ...p, color: c }))}/>
              <div className="flex gap-2">
                <button onClick={handleAddChild} disabled={!addForm.name.trim()} className="flex-1 bg-green-500 hover:bg-green-600 disabled:opacity-40 text-white px-4 py-2 rounded-lg text-sm font-medium transition">✅ Přidat</button>
                <button onClick={() => setAddingChild(null)} className="flex-1 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 px-4 py-2 rounded-lg text-sm font-medium transition">Zrušit</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ══ EDIT MODAL ═════════════════════════════════════════════════════ */}
      {editingNode && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-2xl w-full max-w-sm">
            <h3 className="text-base font-bold mb-4 text-gray-900 dark:text-white">✏️ Upravit: {editingNode.name}</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">Název:</label>
                <input type="text" value={editForm.name} onChange={e => setEditForm(p => ({ ...p, name: e.target.value }))}
                  className="w-full p-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
                  autoFocus onKeyDown={e => e.key === 'Enter' && handleUpdateNode()}/>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1 text-gray-700 dark:text-gray-300">Popis:</label>
                <textarea value={editForm.description} onChange={e => setEditForm(p => ({ ...p, description: e.target.value }))}
                  className="w-full p-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm resize-none focus:outline-none focus:ring-2 focus:ring-blue-400"
                  rows="2" placeholder="Popis…"/>
              </div>
              <ColorPicker colors={predefinedColors} selected={editForm.color} onSelect={c => setEditForm(p => ({ ...p, color: c }))}/>
              <div className="flex gap-2">
                <button onClick={handleUpdateNode} disabled={!editForm.name.trim()} className="flex-1 bg-blue-500 hover:bg-blue-600 disabled:opacity-40 text-white px-4 py-2 rounded-lg text-sm font-medium transition">✅ Uložit</button>
                <button onClick={() => setEditingNode(null)} className="flex-1 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 px-4 py-2 rounded-lg text-sm font-medium transition">Zrušit</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ══ MOVE MODAL ═════════════════════════════════════════════════════ */}
      {movingNode && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-2xl w-full max-w-sm flex flex-col max-h-[70vh]">
            <h3 className="text-base font-bold mb-1 text-gray-900 dark:text-white flex-shrink-0">
              🔀 Přesunout: {movingNode.name}
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-3 flex-shrink-0">
              Vyberte nové umístění
            </p>

            <div className="flex-1 overflow-y-auto border border-gray-200 dark:border-gray-600 rounded-lg p-2 min-h-0">
              {/* root option */}
              <div
                className={`flex items-center gap-2 px-2 py-1.5 rounded text-sm cursor-pointer
                  ${moveTarget === null ? 'bg-blue-100 dark:bg-blue-800/60 text-blue-700 dark:text-blue-300 font-medium' : 'hover:bg-gray-100 dark:hover:bg-gray-700/50 text-gray-700 dark:text-gray-300'}`}
                onClick={() => setMoveTarget(null)}
              >
                <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V9z"/>
                </svg>
                <span>Kořenová úroveň</span>
                {moveTarget === null && <span className="ml-auto text-blue-500">✓</span>}
              </div>
              {hierarchy.map(node => (
                <MoveTreeNode
                  key={node._id}
                  node={node}
                  targetId={moveTarget}
                  onSelect={setMoveTarget}
                  disabledIds={movingNodeDisabledIds}
                />
              ))}
            </div>

            <div className="flex gap-2 mt-4 flex-shrink-0">
              <button onClick={handleMoveNode} className="flex-1 bg-amber-500 hover:bg-amber-600 text-white px-4 py-2 rounded-lg text-sm font-medium transition">
                🔀 Přesunout
              </button>
              <button onClick={() => setMovingNode(null)} className="flex-1 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 px-4 py-2 rounded-lg text-sm font-medium transition">
                Zrušit
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Shared color picker sub-component ───────────────────────────────────────

function ColorPicker({ colors, selected, onSelect }) {
  return (
    <div>
      <label className="block text-sm font-medium mb-1.5 text-gray-700 dark:text-gray-300">Barva:</label>
      <div className="flex flex-wrap gap-2">
        {colors.map(c => (
          <button
            key={c.value}
            onClick={() => onSelect(c.value)}
            className={`w-6 h-6 rounded-full border-2 transition-transform ${selected === c.value ? 'border-gray-900 dark:border-white scale-125' : 'border-transparent hover:scale-110'}`}
            style={{ backgroundColor: c.value }}
            title={c.name}
          />
        ))}
      </div>
    </div>
  );
}

export default MindMapKnowledgeManager;
